import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { message, Button } from "antd";
import { ReactComponent as Back } from "../../../../../assets/svgs/back-button.svg";
import { getApi } from "../../../../../api/api_instance";
import { SearchBar } from "../../../../../components/Elements/Search/SearchBar";
import SectionList from "./SectionList";
import SubsectionList from "./SubSectionList";
import QuestionList from "./QuestionList";
import TextStyles from "../../../../../components/Elements/TextStyles/TextStyles";
import { useParams } from "react-router-dom";
import { ArrowUpOutlined } from "@ant-design/icons";

const ViewForm = ({ sectionurl, subsectionurl, questionurl }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState("-createdAt");
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [subsections, setSubsections] = useState([]);
  const [selectedSubsection, setSelectedSubsection] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [expandAll, setExpandAll] = useState(false);
  const [showGoTop, setShowGoTop] = useState(false);
  const pageSize = 20;
  const navigate = useNavigate();
  const { formId } = useParams();

  const scrollableContainerRef = useRef(null);

  const scrollableContainer = {
    maxHeight: "1000px",
    overflowY: "auto",
    padding: "10px",
    borderRadius: "8px",
  };

  useEffect(() => {
    fetchSections();
  }, []);
  const getDataBlock = (data) => {
    return data.standard || data.custom || {};
  };
  useEffect(() => {
    fetchSections();

    // ✅ Simple window scroll listener
    const handleScroll = () => {
      setShowGoTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ✅ Separate effect for container scroll
  useEffect(() => {
    const handleContainerScroll = () => {
      if (scrollableContainerRef.current) {
        const scrollTop = scrollableContainerRef.current.scrollTop;
        setShowGoTop((prev) => prev || scrollTop > 200);
      }
    };

    const containerElement = scrollableContainerRef.current;
    if (containerElement) {
      containerElement.addEventListener("scroll", handleContainerScroll);
      return () =>
        containerElement.removeEventListener("scroll", handleContainerScroll);
    }
  }, [questions, selectedSubsection]); //

  const fetchSections = async () => {
    setLoading(true);
    try {
      const response = await getApi({
        apiUrl: sectionurl(formId, currentPage, pageSize, sortOrder),
      });

      if (response.success) {
        const { custom = {}, standard = {} } = response.data;

        const customSections = (custom.sections ?? []).map((s) => ({
          ...s,
          type: "custom",
        }));

        const standardSections = (standard.sections ?? []).map((s) => ({
          ...s,
          type: "standard",
        }));

        const combinedSections = [...customSections, ...standardSections];

        setSections(combinedSections);

        if (combinedSections.length > 0) {
          const firstSection = combinedSections[0];
          setSelectedSection(firstSection.sectionId);
          fetchSubsections(firstSection.sectionId, firstSection.type);
        }
      } else {
        console.error("Failed to fetch sections");
      }
    } catch (error) {
      console.error("Error fetching sections", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubsections = async (sectionId) => {
    setLoading(true);
    try {
      const response = await getApi({
        apiUrl: subsectionurl(formId, sectionId),
      });

      if (response.success) {
        const dataBlock = getDataBlock(response.data);
        const subsectionList = dataBlock.subsections ?? [];
        setSubsections(subsectionList);
        setSelectedSection(sectionId);

        if (subsectionList.length > 0) {
          const firstSubsection = subsectionList[0];
          setSelectedSubsection(firstSubsection.subsectionId);
          fetchQuestions(firstSubsection.subsectionId, sectionId);
        }
      } else {
        console.error("Failed to fetch subsections");
      }
    } catch (error) {
      console.error("Error fetching subsections", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = useCallback(
    async (subsectionId = selectedSubsection, sectionId = selectedSection) => {
      if (!subsectionId || !sectionId) return;

      setLoading(true);
      try {
        const response = await getApi({
          apiUrl: questionurl(formId, sectionId, subsectionId, searchTerm),
        });

        if (response.success) {
          const rawQuestions = response.data.questions ?? [];

          const normalizedQuestions = rawQuestions.map((q) => {
            if (q.questionType === "standard" && Array.isArray(q.questions)) {
              return {
                id: q.id || Math.random().toString(),
                title: q.title ?? null,
                questions: q.questions,
              };
            } else {
              return {
                id: q._id || q.id || Math.random().toString(),
                title: null,
                questions: [q],
              };
            }
          });

          setQuestions(normalizedQuestions);
          setTotalQuestions(response.data.totalQuestions ?? 0);
        } else {
          setQuestions([]);
          setTotalQuestions(0);
        }
      } catch (error) {
        console.error("Error fetching questions", error);
        message.error("Failed to fetch questions");
      } finally {
        setLoading(false);
      }
    },
    [searchTerm],
  );

  const getSectionName = (sectionId) => {
    const section = sections.find((s) => s.section === sectionId);
    return section ? section.name : "";
  };

  const scrollToTop = () => {
    // First scroll the container to top
    if (scrollableContainerRef.current) {
      scrollableContainerRef.current.scrollTop = 0;
    }
    // Then scroll window to top
    window.scrollTo({ top: 0, behavior: "smooth" });
    // Hide the button immediately
    setShowGoTop(false);
  };

  // Check if current selected section is a scope section
  const isSelectedSectionScope = () => {
    const sectionName = getSectionName(selectedSection);
    return sectionName === "Scope1&2" || sectionName === "Scope3";
  };

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  return (
    <div className="h-full px-[20px] mb-6">
      <div className="flex justify-end text-xs font-light h-4 ">
        <p>Home</p>
        <span>/</span>
        <p>Dynamic Form</p>
        <span>/</span>
        <TextStyles className="text-[#06513D]">All Questions</TextStyles>
      </div>
      <div className="flex justify-between h-12 pb-2">
        <div className="flex items-center">
          <Back className="cursor-pointer" onClick={() => navigate(-1)} />
          <h3 className="text-[#06513D] mt-2 text-xl">ALL QUESTIONS</h3>
        </div>
      </div>

      <div className="w-full bg-[#fff]  shadow-[0_0_16px_0_#3BA2B91F] mb-6">
        <SectionList
          sections={sections}
          selectedSection={selectedSection}
          onSectionSelect={fetchSubsections}
        />

        {selectedSection && (
          <SubsectionList
            subsections={subsections}
            selectedSubsection={selectedSubsection}
            onSubsectionSelect={(subsectionId) => {
              setSelectedSubsection(subsectionId);
              fetchQuestions(subsectionId, selectedSection);
            }}
          />
        )}
      </div>

      <div className="w-full bg-[#fff]  border border-[#3BA2B959] rounded-[10px] pt-5 shadow-[0_0_16px_0_#3BA2B91F] relative mb-6">
        <div className="mt-4 ml-3">
          <div className="flex flex-col md:flex-row items-center justify-between w-[90%] m-auto gap-4">
            {/* Total Questions + Search */}
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <p className="h-[42px] flex items-center justify-center border border-[#06513D] bg-[#B6D4D933] rounded-md text-[#06513D] text-sm font-light w-40">
                TOTAL QUESTION: {totalQuestions}
              </p>

              <SearchBar
                width="300px"
                onSearch={(value) => setSearchTerm(value)}
                className="sm:w-[250px] md:w-[300px]"
              />
            </div>
            <Button
              type="primary"
              style={{ background: "#06513D" }}
              className="text-[13px] font-light px-6 py-5"
              onClick={() => setExpandAll((prev) => !prev)}
            >
              {expandAll ? "Collapse All" : "Expand All"}
            </Button>
          </div>

          <div style={scrollableContainer}>
            <QuestionList
              loading={loading}
              questions={questions}
              selectedSubsection={selectedSubsection}
              expandAll={expandAll}
            />
            {showGoTop && questions.length > 0 && (
              <Button
                type="primary"
                shape="circle"
                icon={<ArrowUpOutlined />}
                size="large"
                onClick={scrollToTop}
                style={{
                  position: "absolute",
                  bottom: "30px",
                  right: "60px",
                  background: "#06513D",
                  zIndex: 1000,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewForm;
